import { Box, Flex, Menu, Text, Button, MenuButton, useToast } from '@chakra-ui/react';
import { useDispatch, useSelector } from 'react-redux';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import FigmaListTreeIcon from 'components/Icons/FigmaListTreeIcon';
import FigmaMenuDotsIcon from 'components/Icons/FigmaMenuDotsIcon';
import FigmaTriangleDown from 'components/Icons/FigmaTriangleDown';
import FigmaMoveIcon from 'components/Icons/FigmaMoveIcon';
import FigmaCheckCircleIcon from 'components/Icons/FigmaCheckCircleIcon';
import FigmaEditIcon from 'components/Icons/FigmaEditIcon';
import FigmaTrashIcon from 'components/Icons/FigmaTrashIcon';
import DuplicateIconV1 from 'components/Icons/DuplicateIconV1';
import {
  DeleteDimension,
  DuplicateDimension,
  FetchDimensionList,
  getAddDimension,
  getDimensionLoaderStatus,
  getSelectedDimension,
  getSettingsSectionStatus,
  handleBuilderSelectedDimension,
  handleDimensionItemsChange,
  handleSettingsSection,
  UpdateDimensionName,
} from 'redux/BuilderModeSlice';
import apiService from 'services';
import { ActionMenuList } from 'components/ActionMenuList';

import AddDimensionModal from './AddDimensionModal';
import { accent, font, fontSize, neutral, radius, text as textToken } from './designTokens';

const DeleteDimensionWarningModal = lazy(() => import('./DeleteDimensionWarningModal'));

const deleteModalInitial = {
  status: false,
  dimId: null as number | null,
  warningMessage: '',
};

const DimensionItemTab = ({ dimData, itemCount }: any) => {
  const dispatch: any = useDispatch();
  const parameters = useParams().modelId;
  const inputReference: any = useRef(null);
  const toast = useToast();

  const addDimension = useSelector(getAddDimension);
  const selectedDimension: any = useSelector(getSelectedDimension);
  const dimensionLoader = useSelector(getDimensionLoaderStatus);
  const settingsSectionStatus = useSelector(getSettingsSectionStatus);

  const [hover, setHover] = useState<boolean>(false);
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState(deleteModalInitial);
  const [deleteUsageLoading, setDeleteUsageLoading] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);

  const isSelected = selectedDimension?.id === dimData?.id;
  const showActions = hover || isChecked;

  const { attributes, listeners, setNodeRef, transform, isDragging } = useSortable({
    id: dimData?.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    zIndex: isDragging ? '100' : 'auto',
    opacity: isDragging ? 0.3 : 1,
  };

  const handleRenameDimension = (name: string, description: string) => {
    dispatch(
      UpdateDimensionName({
        item_id: dimData?.id,
        request_body: {
          name,
          description,
        },
      }),
    ).then(() => {
      if (parameters) dispatch(FetchDimensionList(parameters));
      setIsRenameModalOpen(false);
    });
  };

  const handleOpenDeleteDimension = async (dimId: number) => {
    setDeleteUsageLoading(true);
    try {
      const response = await apiService.getDimensionUsage(dimId);
      const warningMessage =
        response.data?.warning_message ??
        'Are you sure you want to delete this dimension? This may affect blocks and indicators that use it.';
      setDeleteModal({ status: true, dimId, warningMessage });
    } catch {
      toast({
        title: 'Could not load dimension usage',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setDeleteUsageLoading(false);
    }
  };

  const handleConfirmDeleteDimension = () => {
    if (deleteModal.dimId == null) return;
    const itemid = deleteModal.dimId;
    setDeleteModal(deleteModalInitial);
    dispatch(DeleteDimension(itemid)).then(() => {
      if (parameters)
        dispatch(FetchDimensionList(parameters)).then((response: any) => {
          dispatch(handleBuilderSelectedDimension(response?.payload?.dimensions[0]));
        });
    });
  };

  const handleDimensionClick = (data: any) => {
    dispatch(handleDimensionItemsChange({ dimensionFilters: [] }));
    if (settingsSectionStatus) {
      dispatch(handleSettingsSection());
    }
    // Properties panel deliberately stays open across dimension switches —
    // it re-fetches and shows its own skeleton for the newly selected
    // dimension instead of closing out from under the user.
    dispatch(handleBuilderSelectedDimension(data));
  };

  useEffect(() => {
    const setReference = () => {
      setTimeout(() => {
        if (addDimension && inputReference.current) {
          inputReference.current.focus();
        }
      }, 0);
    };
    setReference();
  }, [addDimension]);

  return (
    <Flex
      alignItems='center'
      gap='8px'
      boxSizing='border-box'
      // Selection used to be a solid accent fill with white text — the single
      // loudest element on the page, and it forced every child icon/label into
      // a second "on purple" colour scheme. The tint alone carries it, keeps
      // the text at full contrast, and means children need no inverted variant.
      background={isSelected ? accent.subtle : neutral.surface}
      border='1px solid transparent'
      borderBottom={`1px solid ${isSelected ? 'transparent' : neutral.borderSubtle}`}
      borderRadius={radius.control}
      // Fixed height so the row never resizes when hovering swaps in the
      // checkbox/move/menu group (that group's tallest element, the 22px
      // menu button, was previously taller than the default item-count +
      // chevron content, causing a visible jump between the two states).
      height='40px'
      px='8px'
      width='100%'
      cursor='pointer'
      ref={setNodeRef}
      style={style}
      onMouseOver={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      position='relative'
      transition='background 0.12s ease'
      boxShadow='none'
      // Hovering a list row shouldn't lift it off the page — a background
      // change is the whole affordance. The purple-tinted shadow also made
      // hover look more emphatic than actual selection.
      //
      // Both rules on the hovered row are cleared so the highlight reads as one
      // clean block: its own bottom border is the line *under* it, and the line
      // *above* it belongs to the preceding row — hence the `:has(+ …:hover)`
      // pass, which is the only way to reach a previous sibling in CSS.
      _hover={{
        background: isSelected ? accent.subtle : neutral.surfaceHover,
        borderBottomColor: 'transparent',
      }}
      // Selection is component state, not a CSS state, so it needs a hook in
      // the DOM for the sibling selector below to reach it.
      data-selected={isSelected ? 'true' : undefined}
      sx={{
        // Same idea for both: the line *under* a row is its own bottom border,
        // the line *above* it belongs to the preceding row, and `:has(+ …)` is
        // the only way to reach a previous sibling in CSS. The row's own
        // bottom border is already cleared — on hover just above, and on
        // selection via the `borderBottom` prop.
        '&:has(+ *:hover), &:has(+ [data-selected])': {
          borderBottomColor: 'transparent',
        },
      }}
      onClick={() => (dimensionLoader ? null : handleDimensionClick(dimData))}
    >
      <FigmaListTreeIcon
        width={14}
        height={14}
        color={isSelected ? textToken.primary : textToken.muted}
      />
      <Text
        flex={1}
        fontSize={fontSize.body}
        fontFamily={font.body}
        fontWeight={isSelected ? '500' : '400'}
        color={textToken.primary}
        whiteSpace='nowrap'
        overflow='hidden'
        textOverflow='ellipsis'
      >
        {dimData?.name}
      </Text>

      {showActions ? (
        <Flex alignItems='center' gap='8px' flexShrink={0}>
          <Box
            as='button'
            onClick={(event: any) => {
              event.stopPropagation();
              setIsChecked((previous) => !previous);
            }}
            width='16px'
            height='16px'
            borderRadius='50%'
            flexShrink={0}
            display='flex'
            alignItems='center'
            justifyContent='center'
            bg={neutral.surface}
            border={isChecked ? 'none' : `1px solid ${neutral.borderStrong}`}
            transition='border-color 0.12s ease'
          >
            {isChecked && <FigmaCheckCircleIcon width={16} height={16} color={accent.solid} />}
          </Box>
          <Box
            as='span'
            cursor='grab'
            display='flex'
            alignItems='center'
            onClick={(event) => event.stopPropagation()}
            {...attributes}
            {...listeners}
          >
            <FigmaMoveIcon width={13} height={13} color={textToken.secondary} />
          </Box>
          <Menu closeOnSelect={true}>
            <MenuButton
              as={Button}
              variant='unstyled'
              border='none'
              background='transparent'
              transition='background 0.12s ease'
              _hover={{ background: neutral.surfaceHover }}
              _active={{ background: neutral.surfaceHover }}
              width='22px'
              height='22px'
              minW='22px'
              minH='22px'
              borderRadius={radius.control}
              display='flex'
              alignItems='center'
              justifyContent='center'
              padding={0}
              opacity={dimensionLoader ? 0.5 : 1}
              onClick={(event) => event.stopPropagation()}
            >
              <FigmaMenuDotsIcon width={13} height={13} color={textToken.secondary} />
            </MenuButton>
            <ActionMenuList
              items={[
                {
                  key: 'rename',
                  label: 'Rename',
                  icon: <FigmaEditIcon width={14} height={14} color={textToken.primary} />,
                  onSelect: () => setIsRenameModalOpen(true),
                },
                {
                  key: 'duplicate',
                  label: 'Duplicate',
                  icon: (
                    <Flex w='14px' h='14px' align='center' justify='center' flexShrink={0}>
                      <DuplicateIconV1 />
                    </Flex>
                  ),
                  onSelect: () =>
                    dispatch(
                      DuplicateDimension({
                        dimId: dimData?.id,
                        data: { name: '' },
                      }),
                    ),
                },
                {
                  key: 'delete',
                  label: 'Delete',
                  icon: <FigmaTrashIcon width={14} height={14} color='#E23F3F' />,
                  color: '#E23F3F',
                  isDisabled: deleteUsageLoading,
                  onSelect: () => handleOpenDeleteDimension(dimData?.id),
                },
              ]}
            />
          </Menu>
        </Flex>
      ) : (
        <>
          {itemCount !== undefined && (
            <Text
              fontSize={fontSize.label}
              fontWeight='400'
              fontFamily={font.body}
              color={isSelected ? textToken.primary : textToken.muted}
              whiteSpace='nowrap'
              flexShrink={0}
            >
              {itemCount} items
            </Text>
          )}
          <Box transform='rotate(-90deg)' display='flex' flexShrink={0}>
            <FigmaTriangleDown
              width={8}
              height={5}
              color={isSelected ? textToken.primary : textToken.muted}
            />
          </Box>
        </>
      )}
      {deleteModal.status ? (
        <Suspense fallback={null}>
          <DeleteDimensionWarningModal
            warningMessage={deleteModal.warningMessage}
            onClose={() => setDeleteModal(deleteModalInitial)}
            onConfirm={handleConfirmDeleteDimension}
          />
        </Suspense>
      ) : null}
      <AddDimensionModal
        isOpen={isRenameModalOpen}
        onClose={() => setIsRenameModalOpen(false)}
        onCreate={handleRenameDimension}
        title='Edit Dimension'
        submitLabel='Save Changes'
        initialName={dimData?.name}
        initialDescription={dimData?.description || ''}
      />
    </Flex>
  );
};

export default DimensionItemTab;
